import * as THREE from "three";

export function buildingDamageStage(hp: number, maxHp: number, completed: boolean): number {
  if (!completed || maxHp <= 0) return 0;
  const health = Math.max(0, hp / maxHp);
  return health > .75 ? 0 : health > .5 ? 1 : health > .25 ? 2 : 3;
}

const flameGeometry = new THREE.ConeGeometry(1, 1, 7);
flameGeometry.translate(0, .5, 0);
const smokeGeometry = new THREE.IcosahedronGeometry(1, 1);
const flameMaterial = new THREE.MeshBasicMaterial({ toneMapped: false });
const smokeMaterial = new THREE.MeshBasicMaterial({ color: 0x38332e, transparent: true, opacity: .24, depthWrite: false });
const transform = new THREE.Object3D();

// Each damaged building owns its deformed geometry; cached healthy models stay intact.
export class BuildingDamage {
  private stage = 0;
  private meshes: { mesh: THREE.Mesh; original: THREE.BufferGeometry; damaged: THREE.BufferGeometry }[] = [];
  private root = new THREE.Group();
  private flames = new THREE.InstancedMesh(flameGeometry, flameMaterial, 24);
  private smoke = new THREE.InstancedMesh(smokeGeometry, smokeMaterial, 12);
  private width = 1;
  private depth = 1;
  private height = 1;

  constructor(private visual: THREE.Object3D) {
    visual.traverse(child => {
      if (!(child instanceof THREE.Mesh)) return;
      const original = child.geometry;
      const damaged = original.clone();
      child.geometry = damaged;
      this.meshes.push({ mesh: child, original, damaged });
    });
    const bounds = new THREE.Box3();
    visual.updateWorldMatrix(true, true);
    const inverse = visual.matrixWorld.clone().invert();
    for (const { mesh, original } of this.meshes) {
      original.computeBoundingBox();
      bounds.union(original.boundingBox!.clone().applyMatrix4(inverse.clone().multiply(mesh.matrixWorld)));
    }
    const size = bounds.getSize(new THREE.Vector3());
    this.width = size.x; this.depth = size.z; this.height = size.y;
    this.root.name = "building-fire";
    // Animated instances move outside their initial bounds; the parent controls visibility.
    this.flames.frustumCulled = this.smoke.frustumCulled = false;
    for (let i = 0; i < 24; i++) this.flames.setColorAt(i, new THREE.Color(i % 2 ? 0xffdf69 : 0xf56b16));
    this.root.add(this.flames, this.smoke);
    visual.add(this.root);
  }

  setHealth(hp: number, maxHp: number, completed: boolean): void {
    const stage = buildingDamageStage(hp, maxHp, completed);
    if (stage === this.stage) return;
    this.stage = stage;
    for (const { mesh, original, damaged } of this.meshes) {
      const source = original.getAttribute("position"), position = damaged.getAttribute("position");
      const sourceColor = original.getAttribute("color"), color = damaged.getAttribute("color");
      const pieces: number[] = original.userData.parts ?? [source.count];
      let start = 0;
      pieces.forEach((count, piece) => {
        const center = new THREE.Vector3();
        for (let i = start; i < start + count; i++) center.add(new THREE.Vector3().fromBufferAttribute(source, i));
        center.divideScalar(count);
        const seed = (Math.sin(piece * 78.233 + center.x * 12.9898 + center.z * 39.34) * 43758.5453) % 1;
        const amount = stage / 3;
        // Foundation remains readable. Selected roof/wall pieces tilt and slump independently.
        const elevation = center.y + mesh.position.y;
        const broken = elevation > .35 && Math.abs(seed) < amount * .8;
        const angle = broken ? seed * amount * 1.5 : 0;
        const drop = broken ? Math.min(Math.max(.2, elevation) * .65, amount * 1.5) : 0;
        for (let i = start; i < start + count; i++) {
          const x = source.getX(i) - center.x, y = source.getY(i) - center.y;
          position.setXYZ(i, center.x + x * Math.cos(angle) - y * Math.sin(angle),
            Math.max(original.boundingBox!.min.y, center.y + x * Math.sin(angle) + y * Math.cos(angle) - drop), source.getZ(i));
          if (sourceColor && color) {
            const char = 1 - amount * (broken ? .7 : .28);
            color.setXYZ(i, sourceColor.getX(i) * char, sourceColor.getY(i) * char, sourceColor.getZ(i) * char);
          }
        }
        start += count;
      });
      position.needsUpdate = true;
      if (color) color.needsUpdate = true;
      damaged.computeVertexNormals(); damaged.computeBoundingSphere();
    }
  }

  update(now: number, visible = true): void {
    this.root.visible = visible && this.stage >= 2;
    if (!this.root.visible) return;
    const time = now / 1000;
    const seats = this.stage === 3 ? 6 : 3;
    this.flames.count = seats * 4;
    this.smoke.count = seats * 2;
    for (let i = 0; i < seats; i++) {
      const angle = i * 2.399;
      const x = Math.cos(angle) * this.width * .29, z = Math.sin(angle) * this.depth * .29;
      const base = this.height * (.22 + (i % 3) * .12);
      for (let j = 0; j < 4; j++) {
        const phase = time * (2.6 + i * .13) + i * 7 + j;
        const pulse = .8 + Math.sin(phase * 2.1) * .2;
        const core = j % 2 ? .58 : 1;
        transform.position.set(x + Math.sin(phase) * .12, base + (j > 1 ? .3 : 0), z + Math.cos(phase) * .12);
        transform.rotation.set(Math.sin(phase) * .17, angle, Math.cos(phase * 1.3) * .22);
        transform.scale.set(.28 * core * pulse, (.85 + this.stage * .22) * pulse * core, .28 * core * pulse);
        transform.updateMatrix(); this.flames.setMatrixAt(i * 4 + j, transform.matrix);
      }
      for (let j = 0; j < 2; j++) {
        const rise = (time * .32 + i * .173 + j * .5) % 1;
        transform.position.set(x + rise * .8, base + .8 + rise * 2.8, z + rise * .3);
        transform.rotation.set(rise, angle, time * .12);
        transform.scale.setScalar((.18 + rise * .65) * Math.sin(Math.PI * rise));
        transform.updateMatrix(); this.smoke.setMatrixAt(i * 2 + j, transform.matrix);
      }
    }
    this.flames.instanceMatrix.needsUpdate = this.smoke.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.visual.remove(this.root);
    this.flames.dispose(); this.smoke.dispose();
    for (const { mesh, original, damaged } of this.meshes) { mesh.geometry = original; damaged.dispose(); }
  }
}
